import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import {
  gerarDocumentoPropostaSolar,
  montarDadosDocumentoPropostaSalva
} from '../utils/propostaDocumento'
import './ConsultaPropostas.css'

// Documento da proposta (PDF/PPTX) é o mesmo da Calculadora Solar:
// src/utils/propostaDocumento. Altere o módulo compartilhado, não duplique aqui.

const ConsultaPropostas = ({ userRole, userId, userName, userEmpresa, onBack, onIniciarEsteira }) => {
  const [propostas, setPropostas] = useState([])
  const [loading, setLoading] = useState(false)
  const [filtros, setFiltros] = useState({
    nomeCliente: '',
    cpf: '',
    dataInicio: '',
    dataFim: '',
    status: 'todas' // todas, 1=ativo, 2=vencida, 3=cancelada, 4=aprovada
  })
  const [propostasFiltradas, setPropostasFiltradas] = useState([])
  const [ordenacao, setOrdenacao] = useState({
    campo: 'data_proposta',
    direcao: 'desc' // asc, desc
  })

  // Estados do modal de detalhes
  const [showDetalhesModal, setShowDetalhesModal] = useState(false)
  const [propostaDetalhes, setPropostaDetalhes] = useState(null)

  // Estados do modal de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalData, setConfirmModalData] = useState({
    type: 'success', // success, error, warning, info
    title: '',
    message: '',
    onConfirm: null,
    confirmLabel: 'OK' // rótulo do botão de confirmação; quando onConfirm existe, pode ser "Excluir" etc.
  })
  const [excluindoProposta, setExcluindoProposta] = useState(false)
  const [gerandoDocumentoPropostaId, setGerandoDocumentoPropostaId] = useState(null)

  // Função para atualizar propostas vencidas
  const atualizarPropostasVencidas = async (propostas) => {
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0) // Zerar horas para comparação correta
    
    const propostasParaAtualizar = propostas.filter(proposta => {
      // Apenas propostas com status "Ativo" (1) que já passaram da data de validade
      if (proposta.status !== 1) return false
      
      const dataValidade = new Date(proposta.data_validade)
      dataValidade.setHours(0, 0, 0, 0)
      
      return dataValidade < hoje
    })

    if (propostasParaAtualizar.length > 0) {
      console.log(`Atualizando ${propostasParaAtualizar.length} proposta(s) vencida(s)...`)
      
      // Atualizar todas as propostas vencidas para status 2 (Vencida)
      for (const proposta of propostasParaAtualizar) {
        try {
          await supabase
            .from('propostas_clientes')
            .update({ status: 2 }) // 2 = Vencida
            .eq('id', proposta.id)
          
          // Atualizar localmente também
          proposta.status = 2
        } catch (error) {
          console.error(`Erro ao atualizar proposta ${proposta.id}:`, error)
        }
      }
    }
  }

  // Função para carregar propostas
  const carregarPropostas = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('propostas_clientes')
        .select(`
          *,
          usuarios_sistema!id_usuario_responsavel!inner (
            id,
            nome,
            email,
            empresa_id
          )
        `)
        .order(ordenacao.campo, { ascending: ordenacao.direcao === 'asc' })

      // Regras de filtro de acesso
      if (userRole === 'administrador') {
        // Administrador vê tudo
      } else if (userRole === 'gerente') {
        // Gerente vê todas as propostas da sua empresa
        if (userEmpresa && userEmpresa.id) {
          query = query.eq('usuarios_sistema.empresa_id', userEmpresa.id)
        } else {
          // Fallback: se não tiver empresa definida, vê apenas as suas
          query = query.eq('id_usuario_responsavel', userId)
        }
      } else {
        // Outros usuários veem apenas as suas
        query = query.eq('id_usuario_responsavel', userId)
      }

      const { data, error } = await query

      if (error) throw error

      // Atualizar propostas vencidas automaticamente
      if (data && data.length > 0) {
        await atualizarPropostasVencidas(data)
      }

      setPropostas(data || [])
    } catch (error) {
      console.error('Erro ao carregar propostas:', error)
      alert('Erro ao carregar propostas: ' + error.message)
    } finally {
      setLoading(false)
    }
  }, [userRole, userId, ordenacao, userEmpresa])

  // Carregar propostas quando o componente monta ou filtros mudam
  useEffect(() => {
    carregarPropostas()
  }, [carregarPropostas])

  // Filtrar propostas
  useEffect(() => {
    let filtradas = [...propostas]

    // Filtro por nome do cliente
    if (filtros.nomeCliente) {
      filtradas = filtradas.filter(proposta =>
        proposta.nome_cliente.toLowerCase().includes(filtros.nomeCliente.toLowerCase())
      )
    }

    // Filtro por CPF
    if (filtros.cpf) {
      filtradas = filtradas.filter(proposta =>
        proposta.cpf && proposta.cpf.includes(filtros.cpf.replace(/\D/g, ''))
      )
    }

    // Filtro por data
    if (filtros.dataInicio) {
      filtradas = filtradas.filter(proposta =>
        new Date(proposta.data_proposta) >= new Date(filtros.dataInicio)
      )
    }

    if (filtros.dataFim) {
      filtradas = filtradas.filter(proposta =>
        new Date(proposta.data_proposta) <= new Date(filtros.dataFim + 'T23:59:59')
      )
    }

    // Filtro por status
    if (filtros.status !== 'todas') {
      filtradas = filtradas.filter(proposta => {
        return proposta.status === parseInt(filtros.status)
      })
    }

    setPropostasFiltradas(filtradas)
  }, [propostas, filtros])

  // Handler para mudança de filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }))
  }

  // Handler para ordenação
  const handleOrdenacao = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'desc' ? 'asc' : 'desc'
    }))
  }

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltros({
      nomeCliente: '',
      cpf: '',
      dataInicio: '',
      dataFim: '',
      status: 'todas'
    })
  }

  // Função para formatar CPF
  const formatCPF = (cpf) => {
    if (!cpf) return '-'
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  // Função para obter informações do status
  const getStatusInfo = (status) => {
    const statusMap = {
      1: { label: 'Ativo', icon: '🟢', color: 'success', class: 'status-ativo' },
      2: { label: 'Vencida', icon: '🟡', color: 'warning', class: 'status-vencida' },
      3: { label: 'Cancelada', icon: '🔴', color: 'danger', class: 'status-cancelada' },
      4: { label: 'Aprovada', icon: '🔵', color: 'info', class: 'status-aprovada' }
    }
    return statusMap[status] || { label: 'Desconhecido', icon: '❓', color: 'secondary', class: 'status-desconhecido' }
  }

  // Função para formatar valor monetário
  const formatValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor)
  }

  // Função para formatar data
  const formatData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Função para calcular dias restantes
  const calcularDiasRestantes = (dataValidade) => {
    const hoje = new Date()
    const validade = new Date(dataValidade)
    const diffTime = validade - hoje
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  // Função para mostrar modal de confirmação
  const showModalConfirmation = (type, title, message, onConfirm = null, confirmLabel = 'OK') => {
    setConfirmModalData({ type, title, message, onConfirm, confirmLabel })
    setShowConfirmModal(true)
  }

  // Fechar modal sem executar onConfirm (cancelar)
  const closeConfirmModal = () => {
    setShowConfirmModal(false)
  }

  // Fechar modal e executar onConfirm (confirmar ação)
  const handleConfirmModalConfirm = () => {
    if (confirmModalData.onConfirm) {
      confirmModalData.onConfirm()
    }
    setShowConfirmModal(false)
  }

  // Excluir proposta (somente administrador)
  const excluirProposta = (proposta) => {
    showModalConfirmation(
      'warning',
      'Excluir proposta',
      `Deseja realmente excluir a proposta #${proposta.id} (${proposta.nome_cliente})?\n\nEsta ação não pode ser desfeita.`,
      () => executarExclusaoProposta(proposta),
      'Excluir'
    )
  }

  const executarExclusaoProposta = async (proposta) => {
    setExcluindoProposta(true)
    try {
      const { error } = await supabase
        .from('propostas_clientes')
        .delete()
        .eq('id', proposta.id)

      if (error) throw error
      await carregarPropostas()
      showModalConfirmation(
        'success',
        'Proposta excluída',
        `A proposta #${proposta.id} (${proposta.nome_cliente}) foi excluída com sucesso.`,
        null
      )
    } catch (error) {
      console.error('Erro ao excluir proposta:', error)
      showModalConfirmation(
        'error',
        'Erro ao excluir',
        `Não foi possível excluir a proposta: ${error.message}`,
        null
      )
    } finally {
      setExcluindoProposta(false)
    }
  }

  // Mesma lógica do botão GERAR da Calculadora Solar (módulo compartilhado)
  const gerarDocumentoProposta = async (proposta) => {
    if (proposta.status !== 1) {
      const statusInfo = getStatusInfo(proposta.status)
      showModalConfirmation(
        'warning',
        '⚠️ Status Inválido',
        `Não é possível gerar documento desta proposta.\n\nStatus atual: ${statusInfo.label}\n\nApenas propostas com status "Ativo" podem ter documento gerado.`,
        null
      )
      return
    }
    setGerandoDocumentoPropostaId(proposta.id)
    try {
      const dados = montarDadosDocumentoPropostaSalva(proposta)
      const result = await gerarDocumentoPropostaSolar({ dados, userEmpresa })
      if (result.tipo === 'pptx') {
        showModalConfirmation(
          'success',
          '✅ Proposta gerada!',
          `Proposta em PowerPoint gerada com sucesso a partir do modelo ${result.templateLabel}.`,
          null
        )
      } else {
        const htmlLinha = result.htmlFileName ? `\nHTML: ${result.htmlFileName}` : ''
        showModalConfirmation(
          'success',
          '✅ Proposta gerada!',
          `Proposta em PDF e HTML gerada com sucesso!\n\nPDF: ${result.fileName}${htmlLinha}`,
          null
        )
      }
    } catch (error) {
      console.error('Erro ao gerar documento da proposta:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Gerar Proposta',
        `Não foi possível gerar a proposta: ${error.message}. Verifique se o template correspondente da empresa está disponível em /docs/.`,
        null
      )
    } finally {
      setGerandoDocumentoPropostaId((id) => (id === proposta.id ? null : id))
    }
  }

  // Função para abrir modal de detalhes
  const handleVerDetalhes = (proposta) => {
    setPropostaDetalhes(proposta)
    setShowDetalhesModal(true)
  }

  // Função para fechar modal de detalhes
  const fecharDetalhesModal = () => {
    setShowDetalhesModal(false)
    setPropostaDetalhes(null)
  }

  // Função para iniciar esteira do cliente
  const handleIniciarEsteira = (proposta) => {
    if (!onIniciarEsteira) {
      alert('Função de iniciar esteira não configurada')
      return
    }

    // Validar se a proposta está ativa
    if (proposta.status !== 1) {
      const statusInfo = getStatusInfo(proposta.status)
      showModalConfirmation(
        'warning',
        '⚠️ Proposta Não Ativa',
        `Não é possível iniciar a esteira desta proposta.\n\nStatus atual: ${statusInfo.label}\n\nApenas propostas com status "Ativo" podem iniciar esteira.`,
        null
      )
      return
    }

    // Preparar dados para preencher no formulário de ClientesII
    const dadosCliente = {
      proposta_id: proposta.id, // ID da proposta para atualizar depois
      nome_completo: proposta.nome_cliente || '',
      cpf: proposta.cpf || '',
      majoracao: proposta.majorado || '',
      valor_total: proposta.total_proposta || ''
    }

    // Chamar callback passando os dados
    onIniciarEsteira(dadosCliente)
  }


  return (
    <div className="consulta-propostas-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>📋 Consulta de Propostas</h1>
            <p>Visualize e gerencie todas as propostas cadastradas no sistema</p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="btn btn-back"
              title="Voltar ao dashboard"
            >
              ← Voltar
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <h3>🔍 Filtros de Busca</h3>
        <div className="filtros-grid">
          <div className="filtro-group">
            <label htmlFor="nome-cliente">Nome do Cliente:</label>
            <input
              type="text"
              id="nome-cliente"
              value={filtros.nomeCliente}
              onChange={(e) => handleFiltroChange('nomeCliente', e.target.value)}
              placeholder="Digite o nome do cliente..."
              className="filtro-input"
            />
          </div>

          <div className="filtro-group">
            <label htmlFor="cpf">CPF:</label>
            <input
              type="text"
              id="cpf"
              value={filtros.cpf}
              onChange={(e) => handleFiltroChange('cpf', e.target.value)}
              placeholder="000.000.000-00"
              className="filtro-input"
              maxLength="14"
            />
          </div>

          <div className="filtro-group">
            <label htmlFor="data-inicio">Data Início:</label>
            <input
              type="date"
              id="data-inicio"
              value={filtros.dataInicio}
              onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
              className="filtro-input"
            />
          </div>

          <div className="filtro-group">
            <label htmlFor="data-fim">Data Fim:</label>
            <input
              type="date"
              id="data-fim"
              value={filtros.dataFim}
              onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
              className="filtro-input"
            />
          </div>

          <div className="filtro-group">
            <label htmlFor="status">Status:</label>
            <select
              id="status"
              value={filtros.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              className="filtro-select"
            >
              <option value="todas">Todas</option>
              <option value="1">🟢 Ativo</option>
              <option value="2">🟡 Vencida</option>
              <option value="3">🔴 Cancelada</option>
              <option value="4">🔵 Aprovada</option>
            </select>
          </div>

          <div className="filtro-actions">
            <button
              onClick={limparFiltros}
              className="btn btn-secondary"
            >
              🗑️ Limpar
            </button>
            <button
              onClick={carregarPropostas}
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? '⏳ Carregando...' : '🔄 Atualizar'}
            </button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="resumo-section">
        <div className="resumo-cards">
          <div className="resumo-card">
            <div className="resumo-icon">📊</div>
            <div className="resumo-content">
              <h4>Total de Propostas</h4>
              <span>{propostasFiltradas.length}</span>
            </div>
          </div>
          <div className="resumo-card">
            <div className="resumo-icon">🟢</div>
            <div className="resumo-content">
              <h4>Propostas Ativas</h4>
              <span>
                {propostasFiltradas.filter(p => p.status === 1).length}
              </span>
            </div>
          </div>
          <div className="resumo-card">
            <div className="resumo-icon">🔵</div>
            <div className="resumo-content">
              <h4>Propostas Aprovadas</h4>
              <span>
                {propostasFiltradas.filter(p => p.status === 4).length}
              </span>
            </div>
          </div>
          <div className="resumo-card">
            <div className="resumo-icon">🟡</div>
            <div className="resumo-content">
              <h4>Propostas Vencidas</h4>
              <span>
                {propostasFiltradas.filter(p => p.status === 2).length}
              </span>
            </div>
          </div>
          <div className="resumo-card">
            <div className="resumo-icon">🔴</div>
            <div className="resumo-content">
              <h4>Propostas Canceladas</h4>
              <span>
                {propostasFiltradas.filter(p => p.status === 3).length}
              </span>
            </div>
          </div>
          <div className="resumo-card resumo-card-valor">
            <div className="resumo-icon">💰</div>
            <div className="resumo-content">
              <h4>Valor Total</h4>
              <span className="valor-total">
                {formatValor(
                  propostasFiltradas.reduce((total, p) => total + p.total_proposta, 0)
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Propostas */}
      <div className="propostas-table-container">
        <div className="table-header">
          <h3>📋 Lista de Propostas</h3>
          <div className="table-actions">
            <span className="results-count">
              {propostasFiltradas.length} proposta(s) encontrada(s)
            </span>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando propostas...</p>
          </div>
        ) : propostasFiltradas.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>Nenhuma proposta encontrada</h3>
            <p>Tente ajustar os filtros ou cadastre uma nova proposta.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="propostas-table">
              <thead>
                <tr>
                  <th onClick={() => handleOrdenacao('id')} className="sortable">
                    ID {ordenacao.campo === 'id' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('nome_cliente')} className="sortable">
                    Cliente {ordenacao.campo === 'nome_cliente' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('cpf')} className="sortable">
                    CPF {ordenacao.campo === 'cpf' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('kwp_proposta')} className="sortable">
                    Projeto (Kwp) {ordenacao.campo === 'kwp_proposta' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('majorado')} className="sortable">
                    Majoração {ordenacao.campo === 'majorado' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('total_proposta')} className="sortable">
                    Valor Total {ordenacao.campo === 'total_proposta' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('data_proposta')} className="sortable">
                    Data Proposta {ordenacao.campo === 'data_proposta' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('data_validade')} className="sortable">
                    Validade {ordenacao.campo === 'data_validade' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleOrdenacao('status')} className="sortable">
                    Status {ordenacao.campo === 'status' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </th>
                  {(userRole === 'administrador' || userRole === 'gerente') && (
                    <th onClick={() => handleOrdenacao('usuarios_sistema.nome')} className="sortable">
                      Responsável {ordenacao.campo === 'usuarios_sistema.nome' && (ordenacao.direcao === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {propostasFiltradas.map((proposta) => {
                  const statusInfo = getStatusInfo(proposta.status)
                  const diasRestantes = calcularDiasRestantes(proposta.data_validade)
                  
                  return (
                    <tr key={proposta.id} className={`proposta-row ${statusInfo.class}`}>
                      <td className="proposta-id">#{proposta.id}</td>
                      <td className="proposta-cliente">{proposta.nome_cliente}</td>
                      <td className="proposta-cpf">{formatCPF(proposta.cpf)}</td>
                      <td className="proposta-kwp">{proposta.kwp_proposta} Kwp</td>
                      <td className="proposta-majoracao">{formatValor(proposta.majorado || 0)}</td>
                      <td className="proposta-valor">{formatValor(proposta.total_proposta)}</td>
                      <td className="proposta-data">{formatData(proposta.data_proposta)}</td>
                      <td className="proposta-validade">
                        {formatData(proposta.data_validade)}
                        {proposta.status === 1 && (
                          <span className="dias-restantes">
                            ({diasRestantes} dias)
                          </span>
                        )}
                      </td>
                      <td className="proposta-status">
                        <span className={`status-badge ${statusInfo.class}`}>
                          {statusInfo.icon} {statusInfo.label}
                        </span>
                      </td>
                      {(userRole === 'administrador' || userRole === 'gerente') && (
                        <td className="proposta-responsavel">
                          {proposta.usuarios_sistema?.nome || 'N/A'}
                        </td>
                      )}
                      <td className="proposta-acoes">
                        <button
                          className="btn btn-sm btn-start"
                          title={proposta.status === 1 ? "Iniciar esteira do cliente" : "Apenas propostas ativas podem iniciar esteira"}
                          onClick={() => handleIniciarEsteira(proposta)}
                          disabled={proposta.status !== 1}
                          style={{ 
                            opacity: proposta.status === 1 ? 1 : 0.5,
                            cursor: proposta.status === 1 ? 'pointer' : 'not-allowed'
                          }}
                        >
                          ▶️
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          title="Ver detalhes da proposta"
                          onClick={() => handleVerDetalhes(proposta)}
                        >
                          👁️
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          title={proposta.status === 1 ? "Gerar proposta (mesma lógica da Calculadora Solar)" : "Apenas propostas ativas podem gerar documento"}
                          onClick={() => gerarDocumentoProposta(proposta)}
                          disabled={proposta.status !== 1 || gerandoDocumentoPropostaId === proposta.id}
                          style={{ 
                            opacity: proposta.status === 1 && gerandoDocumentoPropostaId !== proposta.id ? 1 : 0.5,
                            cursor: proposta.status === 1 && gerandoDocumentoPropostaId !== proposta.id ? 'pointer' : 'not-allowed'
                          }}
                        >
                          {gerandoDocumentoPropostaId === proposta.id ? '⏳' : '📄'}
                        </button>
                        {userRole === 'administrador' && (
                          <button
                            className="btn btn-sm btn-danger"
                            title="Excluir proposta"
                            onClick={() => excluirProposta(proposta)}
                            disabled={excluindoProposta}
                            style={{ marginLeft: '4px' }}
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalhes da Proposta */}
      {showDetalhesModal && propostaDetalhes && (
        <div className="modal-overlay" onClick={fecharDetalhesModal}>
          <div className="modal-content modal-detalhes" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-info">
              <h2>📋 Detalhes da Proposta #{propostaDetalhes.id}</h2>
              <button onClick={fecharDetalhesModal} className="close-button">
                ✕
              </button>
            </div>
            
            <div className="modal-body modal-detalhes-body">
              <div className="detalhes-grid">
                {/* Informações do Cliente */}
                <div className="detalhes-section">
                  <h3>👤 Informações do Cliente</h3>
                  <div className="detalhes-item">
                    <label>Nome:</label>
                    <span>{propostaDetalhes.nome_cliente}</span>
                  </div>
                  {propostaDetalhes.cpf && (
                    <div className="detalhes-item">
                      <label>CPF:</label>
                      <span>{formatCPF(propostaDetalhes.cpf)}</span>
                    </div>
                  )}
                </div>

                {/* Informações Técnicas */}
                <div className="detalhes-section">
                  <h3>⚡ Informações Técnicas</h3>
                  <div className="detalhes-item">
                    <label>Projeto (Kwp):</label>
                    <span>{propostaDetalhes.kwp_proposta} Kwp</span>
                  </div>
                  <div className="detalhes-item">
                    <label>Potência do Inversor:</label>
                    <span>{propostaDetalhes.potencia_inversor} Kw</span>
                  </div>
                  <div className="detalhes-item">
                    <label>Potência do Módulo:</label>
                    <span>{propostaDetalhes.modulo_potencia}W</span>
                  </div>
                  <div className="detalhes-item">
                    <label>Quantidade de Placas:</label>
                    <span>{propostaDetalhes.quantidade_placas} unidades</span>
                  </div>
                </div>

                {/* Informações Financeiras */}
                <div className="detalhes-section">
                  <h3>💰 Informações Financeiras</h3>
                  <div className="detalhes-item">
                    <label>Valor do Kit:</label>
                    <span>{formatValor(propostaDetalhes.valor_kit)}</span>
                  </div>
                  <div className="detalhes-item">
                    <label>Majoração:</label>
                    <span>{formatValor(propostaDetalhes.majorado || 0)}</span>
                  </div>
                  <div className="detalhes-item detalhes-total">
                    <label>Valor Total:</label>
                    <span>{formatValor(propostaDetalhes.total_proposta)}</span>
                  </div>
                </div>

                {/* Informações de Status e Datas */}
                <div className="detalhes-section">
                  <h3>📅 Status e Datas</h3>
                  <div className="detalhes-item">
                    <label>Status:</label>
                    <span className={`status-badge ${getStatusInfo(propostaDetalhes.status).class}`}>
                      {getStatusInfo(propostaDetalhes.status).icon} {getStatusInfo(propostaDetalhes.status).label}
                    </span>
                  </div>
                  <div className="detalhes-item">
                    <label>Data da Proposta:</label>
                    <span>{formatData(propostaDetalhes.data_proposta)}</span>
                  </div>
                  <div className="detalhes-item">
                    <label>Data de Validade:</label>
                    <span>
                      {formatData(propostaDetalhes.data_validade)}
                      {propostaDetalhes.status === 1 && (
                        <span className="dias-restantes">
                          ({calcularDiasRestantes(propostaDetalhes.data_validade)} dias restantes)
                        </span>
                      )}
                    </span>
                  </div>
                  {(userRole === 'administrador' || userRole === 'gerente') && propostaDetalhes.usuarios_sistema && (
                    <div className="detalhes-item">
                      <label>Responsável:</label>
                      <span>{propostaDetalhes.usuarios_sistema.nome}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={fecharDetalhesModal}
                className="btn btn-primary"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={closeConfirmModal}>
          <div className={`modal-content modal-${confirmModalData.type}`} onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-${confirmModalData.type}`}>
              <h2>{confirmModalData.title}</h2>
              <button onClick={closeConfirmModal} className="close-button">
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line', fontSize: '16px', lineHeight: '1.6' }}>
                {confirmModalData.message}
              </p>
            </div>
            
            <div className="modal-footer">
              {confirmModalData.onConfirm ? (
                <>
                  <button onClick={closeConfirmModal} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmModalConfirm}
                    className={`btn btn-${confirmModalData.type === 'warning' ? 'danger' : 'primary'}`}
                    disabled={excluindoProposta}
                  >
                    {excluindoProposta ? '⏳ Excluindo...' : confirmModalData.confirmLabel}
                  </button>
                </>
              ) : (
                <button onClick={closeConfirmModal} className="btn btn-primary">
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConsultaPropostas
